import { Injectable, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  planDetailFromStored,
  planSavePayloadToStored,
  sessionFromStored,
  sessionProgramIdToStored,
  sessionSummaryFromStored,
  WIRE_PROGRAM_ID,
  type FitnessPlanDetail,
  type FitnessPlanProgram,
  type FitnessPlanSessionSummary,
  type FitnessPlanSplit,
  type PlanSavePayload,
  type StoredFitnessPlanDetailWire,
  type StoredFitnessPlanSessionSummaryWire,
  type StoredFitnessPlanSessionWire,
} from '../../features/plans/fitness-plan.domain';
import type {
  FitnessPlanGoal,
  FitnessPlanSessionStatus,
  FitnessPlanSummary,
  FitnessPlanWorkout,
} from '../../features/plans/fitness-plan.types';
import type {
  Equipment,
  EquipmentClassificationSummary,
  EquipmentIdentificationResult,
} from '../../features/gyms/equipment-classification.types';

/** Match ``recognize_equipment_from_image`` Cloud Function timeout; default callable client is 70s. */
const RECOGNIZE_EQUIPMENT_CALLABLE_MS = 300_000;

/** Single-image hint callable (OpenAI); default client timeout is too low on cold start. */
const DESCRIBE_SCAN_CALLABLE_MS = 120_000;

export interface GymSummary {
  gymId: string;
  role?: string;
  name?: string;
}

export interface GymMapPoint {
  gymId: string;
  name: string;
  lat: number;
  lng: number;
  /** When saved from add-gym Places pick. */
  addressFormatted?: string;
}

export interface GymNearbyPoint extends GymMapPoint {
  /** Exact distance from the requested center in metres. */
  distanceM: number;
}

/** Fields returned by ``get_gym``. */
export interface GymEditorPayload {
  gymId: string;
  name: string;
  description: string;
  lat: number;
  lng: number;
  addressFormatted: string;
}

/** One row from ``gyms/{gymId}/equipment`` (via ``list_gym_equipment``). */
export interface GymEquipmentItem {
  equipmentId: string;
  name: string;
  notes: string;
  /** Firebase Storage object path for list thumbnail; empty if none. */
  photoStoragePath?: string;
  /** All equipment photos (first entry matches ``photoStoragePath`` when set). */
  photoStoragePaths?: string[];
  /** True when ``lastRetrievedAt`` was unset before this list call; not stored in Firestore. */
  isNew?: boolean;
  /** Id of the latest classification subdoc, when one has been saved. */
  latestClassificationId?: string;
  /** Small denormalized snippet of the latest classification for list-time use. */
  summary?: EquipmentClassificationSummary;
}

/** Full equipment row from ``get_gym_equipment``. */
export interface GymEquipmentDetail extends GymEquipmentItem {
  classification?: EquipmentIdentificationResult;
}

/** Pass 1 from ``recognize_equipment_identity`` (name/brand + captions). */
export interface RecognizeEquipmentIdentityResult {
  status: string;
  name: string;
  equipment: Equipment;
  imageCaptions?: string[];
  message: string;
}

/** Pass 2 from ``classify_equipment_details`` (full classification form). */
export interface ClassifyEquipmentDetailsResult {
  status: string;
  classification: EquipmentIdentificationResult;
  message: string;
}

/** Result from ``recognize_equipment_from_image`` (legacy single call). */
export interface RecognizeEquipmentResult {
  status: string;
  name: string;
  notes: string;
  /** One caption per uploaded image, same order as ``storagePaths`` sent to the callable. */
  imageCaptions?: string[];
  /** Full structured classification (always present; empty fields when status is ``error``). */
  classification?: EquipmentIdentificationResult;
  message: string;
}

/** Result from ``describe_equipment_scan_image`` (one scan image → short hint). */
export interface DescribeEquipmentScanImageResult {
  status: string;
  text: string;
  message: string;
}

export interface WorkoutPlanResult {
  status: string;
  userId: string;
  goal: string;
  plan: {
    name: string;
    days: Array<{
      day: number;
      title: string;
      exercises: unknown[];
    }>;
  };
  message: string;
}

export type {
  ExerciseRationale,
  ExerciseRationaleGoal,
  FitnessPlanExercise,
  FitnessPlanGoal,
  FitnessPlanGoalSelection,
  FitnessPlanSessionStatus,
  FitnessPlanSet,
  FitnessPlanSummary,
  FitnessPlanWorkout,
  FitnessScheduleKey,
} from '../../features/plans/fitness-plan.types';
export { FITNESS_SCHEDULE_KEYS } from '../../features/plans/fitness-plan.types';

export type {
  FitnessPlanDetail,
  FitnessPlanProgram,
  FitnessPlanSessionSummary,
  FitnessPlanSplit,
  PlanSavePayload,
  StoredFitnessPlanDetailWire,
} from '../../features/plans/fitness-plan.domain';
export {
  planDetailFromStored,
  planSavePayloadToStored,
  sessionFromStored,
  sessionProgramIdFromStored,
  sessionProgramIdToStored,
  sessionSummaryFromStored,
} from '../../features/plans/fitness-plan.domain';

export type GeneratePlanWorkoutResult =
  | { status: 'ok'; workout: FitnessPlanWorkout }
  | { status: string; message: string; workout?: undefined };

export type GeneratePlanProgramResult =
  | { status: 'ok'; workouts: FitnessPlanWorkout[]; split: FitnessPlanSplit }
  | {
      status: string;
      message: string;
      workouts?: undefined;
      split?: undefined;
    };

export type StartPlanProgramGenResult =
  | { status: 'ok'; jobId: string; programId: string }
  | {
      status: string;
      message: string;
      jobId?: undefined;
      programId?: undefined;
    };

export type ProgramGenJobStatus = 'queued' | 'running' | 'done' | 'error';

export interface ProgramGenJob {
  planId: string;
  programId: string;
  status: ProgramGenJobStatus;
  step?: string;
  stepLabel?: string;
  workoutIndex?: number;
  workoutTotal?: number;
  workoutName?: string;
  workoutSubStep?: string;
  error?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** @deprecated Use {@link FitnessPlanSplit}. */
export type FitnessPlanSchedule =
  import('../../features/plans/fitness-plan.domain').FitnessPlanSplit;

/** Logged performance captured when an after-set coach tip was generated. */
export interface FitnessPlanSessionSetCoachTipBasedOn {
  weightKg: number | null;
  reps: number | null;
}

/** One message in the during-set coach thread. */
export interface FitnessPlanSessionDuringSetCoachMessage {
  id: string;
  role: 'coach' | 'user';
  text: string;
  spokenText?: string;
  audioStoragePath?: string;
  spokenWordTimings?: CoachSpokenWordTiming[];
  at: string;
}

/** During-set coach conversation stored on the set. */
export interface FitnessPlanSessionDuringSetCoach {
  messages: FitnessPlanSessionDuringSetCoachMessage[];
}

/** Word-level timing for coach spokenText karaoke. */
export interface CoachSpokenWordTiming {
  word: string;
  startSec: number;
  endSec: number;
}

/** Cached coach tip for a set (text in Firestore; MP3 in Firebase Storage). */
export interface FitnessPlanSessionSetCoachTip {
  comment: string;
  spokenText: string;
  audioMime?: string;
  /** Canonical Storage object path (e.g. users/uid/sessionCoach/...). */
  audioStoragePath?: string;
  generatedAt: string;
  voiceId?: string;
  /** Word timings for spokenText read-along (Whisper at generation). */
  spokenWordTimings?: CoachSpokenWordTiming[];
  /** Present on after-set tips: weight/reps at generation time. */
  basedOn?: FitnessPlanSessionSetCoachTipBasedOn;
  /** Rest-phase tap-to-ask follow-up options (after-set tips only). */
  suggestedPrompts?: string[];
}

export interface FitnessPlanSessionSet {
  setKey: string;
  templateRepMin?: number;
  templateRepMax?: number;
  /** Planned rest from the workout template (immutable target). */
  restSec?: number;
  /** Actual rest taken after log (skip/timer); does not replace ``restSec``. */
  loggedRestSec?: number;
  weightKg: number | null;
  reps: number | null;
  /** True after log + rest (or skip rest) for this set. */
  logged?: boolean;
  /** Set was skipped (no weight/reps); counts toward session completion. */
  skipped?: boolean;
  notes?: string;
  /** Pre-set coach tip (``coachTiming`` beforeSet). */
  beforeSetCoach?: FitnessPlanSessionSetCoachTip;
  /** Mid-set coach thread (live/rep-time messages). */
  duringSetCoach?: FitnessPlanSessionDuringSetCoach;
  /** Rest coach tip after logging (``coachTiming`` afterSet). */
  afterSetCoach?: FitnessPlanSessionSetCoachTip;
}

export interface FitnessPlanSessionExercise {
  exerciseId: string;
  exerciseName: string;
  order: number;
  sets: FitnessPlanSessionSet[];
  /** Exercise overview coach tip (before first set; shown in every set thread). */
  exerciseOverviewCoach?: FitnessPlanSessionSetCoachTip;
}

/** In-progress rest timer persisted so tab switches can resume. */
export interface FitnessPlanSessionActiveRest {
  exerciseIndex: number;
  setIndex: number;
  /** ISO-8601 UTC when rest ends. */
  endsAt: string;
  /** ISO-8601 UTC when rest started (for elapsed on finish). */
  startedAt: string;
}

export interface FitnessPlanSessionExerciseAnalysis {
  exerciseId: string;
  comment: string;
}

export interface FitnessPlanSessionAnalysis {
  headline: string;
  summary: string;
  highlights: string[];
  suggestions: string[];
  cautions: string[];
  exerciseFeedback?: FitnessPlanSessionExerciseAnalysis[];
}

export interface FitnessPlanSession {
  sessionId: string;
  programId: string;
  workoutId: string;
  workoutName: string;
  status: FitnessPlanSessionStatus;
  startedAt: string;
  finishedAt?: string | null;
  /** Snapshot from session start (when coach tips are enabled). */
  coachName?: string;
  coachId?: string;
  coachAvatarStoragePath?: string;
  exercises: FitnessPlanSessionExercise[];
  activeRest?: FitnessPlanSessionActiveRest | null;
  /** Saved mid-set cheers — reused on every set in this session. */
  customEncouragementCheers?: CoachEncouragementClip[];
  /** Workout-wide thumbs-up/down cheer preferences for mid-set generation. */
  encouragementCheerPreferences?: CoachEncourageCheerPreferences | null;
  /** Mid-set cheer focus chosen at workout start. */
  encouragementCheerScope?:
    | 'look_only'
    | 'form_only'
    | 'both'
    | 'exercise_only';
  /** Allow occasional mild swearing in generated cheers. */
  encouragementCheerProfanityEnabled?: boolean;
  /** Coach + session fields for in-progress settings (future AI generations). */
  coachSettings?: FitnessPlanSessionCoachSettings;
  aiAnalysis?: FitnessPlanSessionAnalysis | null;
  aiAnalysisAt?: string | null;
}

export type AnalyzePlanSessionResult =
  | {
      status: 'ok';
      analysis: FitnessPlanSessionAnalysis;
      analyzedAt: string;
    }
  | { status: string; message: string; analysis?: undefined };

export type AnalyzeCoachTipResult =
  | {
      status: 'ok';
      comment: string;
      spokenText: string;
      audioMime: string;
      audioStoragePath: string;
      cached?: boolean;
      /** Present only on fresh generation for immediate playback. */
      audioBase64?: string;
      /** Word timings for spokenText read-along. */
      spokenWordTimings?: CoachSpokenWordTiming[];
      /** Non-fatal Whisper failure while generating read-along timings. */
      readAlongWarning?: string;
      /** After-set tips: weight/reps at generation time. */
      basedOn?: FitnessPlanSessionSetCoachTipBasedOn;
      suggestedPrompts?: string[];
    }
  | { status: 'error'; message: string };

export type AnalyzeCoachFollowupResult =
  | {
      status: 'ok';
      userMessage: FitnessPlanSessionDuringSetCoachMessage;
      coachMessage: FitnessPlanSessionDuringSetCoachMessage;
      audioMime: string;
      audioBase64?: string;
      readAlongWarning?: string;
    }
  | { status: 'error'; message: string };

export interface CoachEncouragementClip {
  id: string;
  spokenText: string;
  audioMime: string;
  audioBase64?: string;
  audioStoragePath?: string;
  custom?: boolean;
  /** When set, cheer only applies to this exercise in the workout. */
  exerciseId?: string;
}

export type CoachEncourageTone = 'standard' | 'homoerotic';

export interface CoachEncourageFeedbackAnalysis {
  likedThemes: string[];
  dislikedThemes: string[];
  mustIncludeMotifs: string[];
  mustAvoidMotifs: string[];
  generationBrief: string;
}

export interface CoachEncourageCheerPreferences {
  likedCheerTexts: string[];
  dislikedCheerTexts: string[];
  likedThemes: string[];
  dislikedThemes: string[];
  mustIncludeMotifs: string[];
  mustAvoidMotifs: string[];
  generationBrief: string;
  updatedAt?: string;
}

export type AddCoachEncouragementCheerResult =
  | { status: 'ok'; clip: CoachEncouragementClip }
  | { status: 'error'; message: string };

export type FavoriteCoachEncouragementCheerResult =
  | { status: 'ok'; clip: CoachEncouragementClip; alreadySaved?: boolean }
  | { status: 'error'; message: string };

export type RemoveCoachEncouragementCheerResult =
  | { status: 'ok' }
  | { status: 'error'; message: string };

export type AnalyzeCoachEncouragementResult =
  | {
      status: 'ok';
      clips: CoachEncouragementClip[];
      feedbackAnalysis?: CoachEncourageFeedbackAnalysis;
      preferences?: CoachEncourageCheerPreferences;
    }
  | { status: 'error'; message: string };

export type SyncEncouragementCheerPreferencesResult =
  | {
      status: 'ok';
      preferences?: CoachEncourageCheerPreferences;
      feedbackAnalysis?: CoachEncourageFeedbackAnalysis;
    }
  | { status: 'error'; message: string };

/** @deprecated Use {@link AnalyzeCoachTipResult}. */
export type AnalyzeRestCoachResult = AnalyzeCoachTipResult;

export type StartPlanSessionWireResult = {
  session: StoredFitnessPlanSessionWire;
  planId?: string;
  existing?: boolean;
};

export type StartPlanSessionResult = {
  session: FitnessPlanSession;
  planId?: string;
  existing?: boolean;
};

export type ActivePlanSessionResult = {
  session: FitnessPlanSession | null;
  planId: string | null;
};

/** Coach snapshot editable during an in-progress session. */
export interface FitnessPlanSessionCoachSettings {
  personalityId: string;
  motivationStyle: string;
  relationship: string;
  hypeEnabled: boolean;
  spicyEnabled: boolean;
  voiceId: string;
  voiceVibeId: string;
  voiceDeliveryNotes: string;
  traits: string[];
  styles: string[];
  kinks: string[];
  callNames: string[];
  lookDescriptors: string[];
  focusNotes: string;
  backgroundNotes: string;
  appearanceNotes: string;
  customNotes: string;
  sessionDirections: string;
  encouragementCheerScope: 'look_only' | 'form_only' | 'exercise_only';
  encouragementCheerProfanityEnabled: boolean;
}

export interface FitnessCoach {
  coachId: string;
  name: string;
  tagline: string;
  roleLabel: string;
  personalityId: string;
  motivationStyle: string;
  relationship: string;
  hypeEnabled: boolean;
  spicyEnabled: boolean;
  voiceId: string;
  voiceVibeId: string;
  voiceDeliveryNotes: string;
  traits: string[];
  styles: string[];
  kinks: string[];
  callNames: string[];
  lookDescriptors: string[];
  focusNotes: string;
  backgroundNotes: string;
  appearanceNotes: string;
  customNotes: string;
  avatarStoragePath: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export type ListCoachesResult = {
  coaches: FitnessCoach[];
  defaultCoachId: string | null;
};

export type CoachAvatarJobStatus =
  | 'pending'
  | 'queued'
  | 'running'
  | 'done'
  | 'error';

export interface CoachAvatarJob {
  jobId: string;
  status: CoachAvatarJobStatus;
  coachId: string;
  avatarStoragePath: string | null;
  error: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export type GenerateCoachAvatarResult = {
  status: 'ok' | 'error';
  jobId?: string;
  alreadyActive?: boolean;
  message?: string;
};

export type GetCoachAvatarJobResult = {
  status: 'ok';
  job: CoachAvatarJob;
};

@Injectable({
  providedIn: 'root',
})
export class CallableApiService {
  private readonly functions = inject(Functions);

  /** Create a new gym (never send ``gymId``). */
  readonly saveGym = httpsCallable<
    {
      name: string;
      lat: number;
      lng: number;
      description?: string;
      addressFormatted?: string;
    },
    { gymId: string; name: string }
  >(this.functions, 'save_gym');

  readonly getGym = httpsCallable<{ gymId: string }, GymEditorPayload>(
    this.functions,
    'get_gym',
  );

  readonly updateGym = httpsCallable<
    {
      gymId: string;
      name: string;
      lat: number;
      lng: number;
      description?: string;
      addressFormatted?: string;
    },
    { gymId: string; name: string }
  >(this.functions, 'update_gym');

  readonly listGymEquipment = httpsCallable<
    { gymId: string },
    { equipment: GymEquipmentItem[] }
  >(this.functions, 'list_gym_equipment');

  readonly getGymEquipment = httpsCallable<
    { gymId: string; equipmentId: string },
    GymEquipmentDetail
  >(this.functions, 'get_gym_equipment');

  readonly addGymEquipment = httpsCallable<
    {
      gymId: string;
      name: string;
      notes?: string;
      photoStoragePath?: string;
      photoStoragePaths?: string[];
      classification?: EquipmentIdentificationResult;
    },
    {
      equipmentId: string;
      name: string;
      notes: string;
      photoStoragePath?: string;
      photoStoragePaths?: string[];
      latestClassificationId?: string;
      classification?: EquipmentIdentificationResult;
    }
  >(this.functions, 'add_gym_equipment');

  readonly deleteGymEquipment = httpsCallable<
    { gymId: string; equipmentId: string },
    { deleted: boolean; equipmentId: string }
  >(this.functions, 'delete_gym_equipment');

  readonly joinGymByCode = httpsCallable<
    { joinCode: string },
    { gymId: string; joined: boolean }
  >(this.functions, 'join_gym_by_code');

  readonly leaveGym = httpsCallable<
    { gymId: string },
    { gymId: string; left: boolean }
  >(this.functions, 'leave_gym');

  readonly listJoinedGyms = httpsCallable<void, { gyms: GymSummary[] }>(
    this.functions,
    'list_joined_gyms',
  );

  readonly listGymsMap = httpsCallable<
    { lat?: number; lng?: number } | void,
    { gyms: Array<GymMapPoint | GymNearbyPoint> }
  >(this.functions, 'list_gyms_map');

  readonly listGymsNearby = httpsCallable<
    { lat: number; lng: number; radiusM: number },
    { gyms: GymNearbyPoint[] }
  >(this.functions, 'list_gyms_nearby');

  /** Pass 1: equipment identity + image captions (OpenAI vision). */
  readonly recognizeEquipmentIdentity = httpsCallable<
    {
      gymId: string;
      storagePaths: string[];
      context?: string;
      imageContexts?: string[];
    },
    RecognizeEquipmentIdentityResult
  >(this.functions, 'recognize_equipment_identity', {
    timeout: RECOGNIZE_EQUIPMENT_CALLABLE_MS,
  });

  /** Pass 2: full classification after member confirms identity. */
  readonly classifyEquipmentDetails = httpsCallable<
    {
      gymId: string;
      storagePaths: string[];
      confirmedEquipment: Equipment;
      context?: string;
      imageContexts?: string[];
    },
    ClassifyEquipmentDetailsResult
  >(this.functions, 'classify_equipment_details', {
    timeout: RECOGNIZE_EQUIPMENT_CALLABLE_MS,
  });

  /** Legacy single call (pass 1 + 2). Prefer ``recognizeEquipmentIdentity`` + ``classifyEquipmentDetails``. */
  readonly recognizeEquipmentFromImage = httpsCallable<
    {
      gymId: string;
      storagePaths: string[];
      context?: string;
      /** Same order as ``storagePaths``; optional per-photo member hints. */
      imageContexts?: string[];
    },
    RecognizeEquipmentResult
  >(this.functions, 'recognize_equipment_from_image', {
    timeout: RECOGNIZE_EQUIPMENT_CALLABLE_MS,
  });

  /** One uploaded scan path → short plain-text hint (prefill per-photo context on identify). */
  readonly describeEquipmentScanImage = httpsCallable<
    { gymId: string; storagePath: string },
    DescribeEquipmentScanImageResult
  >(this.functions, 'describe_equipment_scan_image', {
    timeout: DESCRIBE_SCAN_CALLABLE_MS,
  });

  readonly generateWorkoutPlan = httpsCallable<
    { goal: string },
    WorkoutPlanResult
  >(this.functions, 'generate_workout_plan');

  /** Returns one normalized workout ready to merge into ``plan.workouts``. */
  readonly generatePlanWorkout = httpsCallable<
    {
      planId: string;
      workoutName?: string;
      workoutType?: string;
      gymIds?: string[];
    },
    GeneratePlanWorkoutResult
  >(this.functions, 'generate_plan_workout', { timeout: 120_000 });

  readonly generatePlanProgram = httpsCallable<
    {
      planId: string;
      name?: string;
      description?: string;
    },
    GeneratePlanProgramResult
  >(this.functions, 'generate_plan_program', { timeout: 540_000 });

  readonly startPlanProgramGen = httpsCallable<
    {
      planId: string;
      name?: string;
      description?: string;
      /** Training days per week (3–6) when starting AI generation. */
      weeklyFrequency?: number;
      /** Retry generation on an existing empty / failed program shell. */
      programId?: string;
    },
    StartPlanProgramGenResult
  >(this.functions, 'start_plan_program_gen', { timeout: 30_000 });

  readonly listFitnessPlans = httpsCallable<
    void,
    { plans: FitnessPlanSummary[] }
  >(this.functions, 'list_fitness_plans');

  readonly getFitnessPlan = httpsCallable<
    { planId: string },
    StoredFitnessPlanDetailWire
  >(this.functions, 'get_fitness_plan');

  /** Loads a plan; maps legacy wire fields to domain programs / split. */
  async fetchFitnessPlan(planId: string): Promise<FitnessPlanDetail> {
    const res = await this.getFitnessPlan({ planId: planId.trim() });
    return planDetailFromStored(res.data);
  }

  private readonly saveFitnessPlanWire = httpsCallable<
    ReturnType<typeof planSavePayloadToStored>,
    { planId: string; name: string }
  >(this.functions, 'save_fitness_plan');

  async saveFitnessPlan(
    payload: PlanSavePayload,
  ): Promise<{ planId: string; name: string }> {
    const res = await this.saveFitnessPlanWire(
      planSavePayloadToStored(payload),
    );
    return res.data;
  }

  readonly deleteFitnessPlan = httpsCallable<
    { planId: string },
    { deleted: boolean; planId: string }
  >(this.functions, 'delete_fitness_plan');

  readonly deleteFitnessPlanProgram = httpsCallable<
    { planId: string; programId: string },
    {
      deleted: boolean;
      planId: string;
      programId: string;
      activeProgramId: string | null;
    }
  >(this.functions, 'delete_fitness_plan_program');

  readonly listPlanSessions = httpsCallable<
    {
      planId: string;
    } & Partial<Record<typeof WIRE_PROGRAM_ID, string>> & {
        status?: FitnessPlanSessionStatus;
        limit?: number;
      },
    { sessions: StoredFitnessPlanSessionSummaryWire[] }
  >(this.functions, 'list_plan_sessions');

  readonly getPlanSession = httpsCallable<
    { planId: string; sessionId: string },
    { session: StoredFitnessPlanSessionWire }
  >(this.functions, 'get_plan_session');

  readonly listCoaches = httpsCallable<void, ListCoachesResult>(
    this.functions,
    'list_coaches',
  );

  readonly saveCoach = httpsCallable<
    {
      coachId?: string;
      name: string;
      tagline?: string;
      roleLabel?: string;
      personalityId?: string;
      motivationStyle?: string;
      relationship?: string;
      hypeEnabled?: boolean;
      spicyEnabled?: boolean;
      voiceId?: string;
      voiceVibeId?: string;
      voiceDeliveryNotes?: string;
      traits?: string[];
      styles?: string[];
      kinks?: string[];
      callNames?: string[];
      lookDescriptors?: string[];
      focusNotes?: string;
      backgroundNotes?: string;
      appearanceNotes?: string;
      customNotes?: string;
      avatarStoragePath?: string | null;
      setAsDefault?: boolean;
    },
    { coach: FitnessCoach }
  >(this.functions, 'save_coach');

  readonly deleteCoach = httpsCallable<
    { coachId: string },
    { deleted: boolean; coachId: string }
  >(this.functions, 'delete_coach');

  readonly generateCoachAvatar = httpsCallable<
    { coachId: string; seed?: number },
    GenerateCoachAvatarResult
  >(this.functions, 'generate_coach_avatar');

  readonly getCoachAvatarJob = httpsCallable<
    { jobId: string },
    GetCoachAvatarJobResult
  >(this.functions, 'get_coach_avatar_job');

  readonly startPlanSession = httpsCallable<
    {
      planId: string;
    } & Record<typeof WIRE_PROGRAM_ID, string> & {
        workoutId: string;
        coachId: string;
        sessionDirections?: string;
        personalityId?: string;
        motivationStyle?: string;
        relationship?: string;
        hypeEnabled?: boolean;
        spicyEnabled?: boolean;
        voiceId?: string;
        voiceVibeId?: string;
        voiceDeliveryNotes?: string;
        traits?: string[];
        styles?: string[];
        kinks?: string[];
        callNames?: string[];
        lookDescriptors?: string[];
        focusNotes?: string;
        backgroundNotes?: string;
        appearanceNotes?: string;
        customNotes?: string;
        encouragementCheerScope?: 'look_only' | 'form_only' | 'exercise_only';
        encouragementCheerProfanityEnabled?: boolean;
      },
    StartPlanSessionWireResult
  >(this.functions, 'start_plan_session');

  readonly savePlanSession = httpsCallable<
    {
      planId: string;
      sessionId: string;
      exercises: FitnessPlanSessionExercise[];
      status?: FitnessPlanSessionStatus;
      /** Pass ``null`` to clear; omit to leave unchanged. */
      activeRest?: FitnessPlanSessionActiveRest | null;
    },
    { session: StoredFitnessPlanSessionWire }
  >(this.functions, 'save_plan_session');

  readonly updateSessionCoachSettings = httpsCallable<
    {
      planId: string;
      sessionId: string;
      saveToCoach?: boolean;
      /** Backfill when legacy session docs lack coachId. */
      coachId?: string;
    } & FitnessPlanSessionCoachSettings,
    { session: StoredFitnessPlanSessionWire }
  >(this.functions, 'update_plan_session_coach_settings');

  readonly completePlanSession = httpsCallable<
    {
      planId: string;
      sessionId: string;
      exercises: FitnessPlanSessionExercise[];
    },
    { session: StoredFitnessPlanSessionWire }
  >(this.functions, 'complete_plan_session');

  readonly discardPlanSession = httpsCallable<
    {
      planId: string;
      sessionId: string;
      exercises?: FitnessPlanSessionExercise[];
    },
    { session: StoredFitnessPlanSessionWire }
  >(this.functions, 'discard_plan_session');

  readonly deletePlanSession = httpsCallable<
    { planId: string; sessionId: string },
    { deleted: boolean }
  >(this.functions, 'delete_plan_session');

  readonly analyzePlanSession = httpsCallable<
    { planId: string; sessionId: string },
    AnalyzePlanSessionResult
  >(this.functions, 'analyze_plan_session', { timeout: 120_000 });

  readonly analyzeRestCoach = httpsCallable<
    {
      planId: string;
      sessionId: string;
      exerciseIndex: number;
      setIndex: number;
    },
    AnalyzeCoachTipResult
  >(this.functions, 'analyze_rest_coach', { timeout: 120_000 });

  readonly analyzeSetCoach = httpsCallable<
    {
      planId: string;
      sessionId: string;
      exerciseIndex: number;
      setIndex: number;
      forceRefresh?: boolean;
    },
    AnalyzeCoachTipResult
  >(this.functions, 'analyze_set_coach', { timeout: 120_000 });

  readonly analyzeExerciseOverviewCoach = httpsCallable<
    {
      planId: string;
      sessionId: string;
      exerciseIndex: number;
      forceRefresh?: boolean;
    },
    AnalyzeCoachTipResult
  >(this.functions, 'analyze_exercise_overview_coach', { timeout: 120_000 });

  readonly analyzeCoachFollowup = httpsCallable<
    {
      planId: string;
      sessionId: string;
      exerciseIndex: number;
      setIndex: number;
      promptText: string;
    },
    AnalyzeCoachFollowupResult
  >(this.functions, 'analyze_coach_followup', { timeout: 120_000 });

  readonly analyzeCoachEncouragement = httpsCallable<
    {
      planId: string;
      sessionId: string;
      exerciseIndex: number;
      setIndex: number;
      tone?: CoachEncourageTone;
      encouragementCheerScope?: 'look_only' | 'form_only' | 'exercise_only';
      encouragementCheerProfanityEnabled?: boolean;
      existingCheers?: string[];
      likedCheers?: string[];
      dislikedCheers?: string[];
    },
    AnalyzeCoachEncouragementResult
  >(this.functions, 'analyze_coach_encouragement', { timeout: 180_000 });

  readonly addCoachEncouragementCheer = httpsCallable<
    {
      planId: string;
      sessionId: string;
      spokenText: string;
      exerciseId?: string;
    },
    AddCoachEncouragementCheerResult
  >(this.functions, 'add_coach_encouragement_cheer', { timeout: 60_000 });

  readonly favoriteCoachEncouragementCheer = httpsCallable<
    {
      planId: string;
      sessionId: string;
      spokenText: string;
      audioBase64?: string;
      exerciseId?: string;
    },
    FavoriteCoachEncouragementCheerResult
  >(this.functions, 'favorite_coach_encouragement_cheer', { timeout: 60_000 });

  readonly removeCoachEncouragementCheer = httpsCallable<
    {
      planId: string;
      sessionId: string;
      cheerId: string;
    },
    RemoveCoachEncouragementCheerResult
  >(this.functions, 'remove_coach_encouragement_cheer', { timeout: 60_000 });

  readonly syncEncouragementCheerPreferences = httpsCallable<
    {
      planId: string;
      sessionId: string;
      likedCheers?: string[];
      dislikedCheers?: string[];
    },
    SyncEncouragementCheerPreferencesResult
  >(this.functions, 'sync_encouragement_cheer_preferences', {
    timeout: 60_000,
  });

  readonly getActivePlanSession = httpsCallable<
    { planId: string } & Record<typeof WIRE_PROGRAM_ID, string>,
    { session: StoredFitnessPlanSessionWire | null; planId: string | null }
  >(this.functions, 'get_active_plan_session');

  async fetchPlanSessions(
    planId: string,
    opts?: {
      programId?: string;
      status?: FitnessPlanSessionStatus;
      limit?: number;
    },
  ): Promise<FitnessPlanSessionSummary[]> {
    const res = await this.listPlanSessions({
      planId: planId.trim(),
      ...(opts?.programId
        ? sessionProgramIdToStored(opts.programId.trim())
        : {}),
      status: opts?.status,
      limit: opts?.limit,
    });
    return (res.data.sessions ?? []).map(sessionSummaryFromStored);
  }

  async fetchPlanSession(
    planId: string,
    sessionId: string,
  ): Promise<FitnessPlanSession> {
    const res = await this.getPlanSession({
      planId: planId.trim(),
      sessionId: sessionId.trim(),
    });
    return sessionFromStored(res.data.session) as FitnessPlanSession;
  }

  async fetchCoaches(): Promise<ListCoachesResult> {
    const res = await this.listCoaches();
    return res.data;
  }

  async startPlanSessionForProgram(
    planId: string,
    programId: string,
    workoutId: string,
    coachId: string,
    coachSettings?: {
      personalityId?: string;
      motivationStyle?: string;
      relationship?: string;
      hypeEnabled?: boolean;
      spicyEnabled?: boolean;
      voiceId?: string;
      voiceVibeId?: string;
      voiceDeliveryNotes?: string;
      traits?: string[];
      styles?: string[];
      kinks?: string[];
      callNames?: string[];
      lookDescriptors?: string[];
      focusNotes?: string;
      backgroundNotes?: string;
      appearanceNotes?: string;
      customNotes?: string;
      sessionDirections?: string;
      encouragementCheerScope?: 'look_only' | 'form_only' | 'exercise_only';
      encouragementCheerProfanityEnabled?: boolean;
    },
  ): Promise<StartPlanSessionResult> {
    const directions = coachSettings?.sessionDirections?.trim() || undefined;
    const callNames = (coachSettings?.callNames ?? [])
      .map((name) => name.trim())
      .filter((name) => name.length > 0);
    const lookDescriptors = (coachSettings?.lookDescriptors ?? [])
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    const focusNotes = coachSettings?.focusNotes?.trim() || undefined;
    const backgroundNotes = coachSettings?.backgroundNotes?.trim() || undefined;
    const appearanceNotes = coachSettings?.appearanceNotes?.trim() || undefined;
    const customNotes = coachSettings?.customNotes?.trim() || undefined;
    const voiceDeliveryNotes =
      coachSettings?.voiceDeliveryNotes?.trim() || undefined;
    const res = await this.startPlanSession({
      planId: planId.trim(),
      ...sessionProgramIdToStored(programId.trim()),
      workoutId: workoutId.trim(),
      coachId: coachId.trim(),
      sessionDirections: directions,
      personalityId: coachSettings?.personalityId,
      motivationStyle: coachSettings?.motivationStyle,
      relationship: coachSettings?.relationship,
      hypeEnabled: coachSettings?.hypeEnabled,
      spicyEnabled: coachSettings?.spicyEnabled,
      voiceId: coachSettings?.voiceId,
      voiceVibeId: coachSettings?.voiceVibeId,
      voiceDeliveryNotes,
      traits: coachSettings?.traits,
      styles: coachSettings?.styles,
      kinks: coachSettings?.kinks,
      callNames,
      lookDescriptors,
      focusNotes,
      backgroundNotes,
      appearanceNotes,
      customNotes,
      encouragementCheerScope: coachSettings?.encouragementCheerScope,
      encouragementCheerProfanityEnabled:
        coachSettings?.encouragementCheerProfanityEnabled === true
          ? true
          : undefined,
    });
    const data = res.data;
    return {
      ...data,
      session: data.session
        ? (sessionFromStored(data.session) as FitnessPlanSession)
        : data.session,
    };
  }

  async updatePlanSessionCoachSettings(
    planId: string,
    sessionId: string,
    settings: FitnessPlanSessionCoachSettings,
    options?: { saveToCoach?: boolean; coachId?: string },
  ): Promise<FitnessPlanSession> {
    const coachId = options?.coachId?.trim();
    const res = await this.updateSessionCoachSettings({
      planId: planId.trim(),
      sessionId: sessionId.trim(),
      ...settings,
      saveToCoach: options?.saveToCoach === true ? true : undefined,
      coachId: coachId || undefined,
    });
    return sessionFromStored(res.data.session) as FitnessPlanSession;
  }

  async fetchActivePlanSession(
    planId: string,
    programId: string,
  ): Promise<ActivePlanSessionResult> {
    const res = await this.getActivePlanSession({
      planId: planId.trim(),
      ...sessionProgramIdToStored(programId.trim()),
    });
    const data = res.data;
    return {
      planId: data.planId,
      session: data.session
        ? (sessionFromStored(data.session) as FitnessPlanSession)
        : null,
    };
  }
}
